import { schema } from 'normalizr';
import { Offer, Tenant, TenantData } from '../types';
import { sortBy, first } from 'lodash';

export const offers = new schema.Entity('offers');
export const charges = new schema.Entity('charges');
export const flagDetails = new schema.Entity('flagDetails');

export const tenants = new schema.Entity(
  'tenants',
  {
    bkin_offers: new schema.Array(offers),
    other_charges: new schema.Array(charges),
    flag_details: flagDetails,
  },
  {
    idAttribute: 'Tenant_Code',
    processStrategy: (value: Tenant, parent: TenantData[], _) => {
      const processData = Object.assign({}, value);
      const currentTenant = parent.find(
        (ele: TenantData) => ele.Tenant_Code === processData.Tenant_Code
      );
      processData.StickinessScore =
        currentTenant?.bkin_offers && currentTenant?.bkin_offers.length > 0
          ? currentTenant?.bkin_offers[0].StickinessScore
          : 1;
      processData.IsApproved =
        currentTenant?.bkin_offers?.every((ele: Offer) => ele.IsApproved) ||
        false;
      processData.Exclude =
        currentTenant?.bkin_offers?.every((ele: Offer) => ele.Exclude) || false;
      processData.Uploaded =
        currentTenant?.bkin_offers?.every((ele: Offer) => ele.Uploaded) ||
        false;
      processData.IsFlagged = currentTenant?.flag_details
        ? !currentTenant?.flag_details?.Resolved
        : false;
      return processData;
    },
  }
);

const separator = '~~||~~';

export const generateLeaseKey = (
  propertyCode: string,
  unit_Type: string
): string => `${propertyCode}${separator}${unit_Type}`;

export const leaseKeySeparator = (leaseKey: string) => {
  const [property_code, unit_Type] = leaseKey.split(separator);
  return {
    property_code,
    unit_Type,
  };
};

export const leasePrice = new schema.Entity('NewLeasePrice', undefined, {
  idAttribute: (value: any, parent: any, key: string): string => {
    return `${value.Property_Code}${separator}${value.UnitType}`;
  },
});

export const demand = new schema.Entity('Demand', undefined, {
  idAttribute: (value: any, parent: any, key: string): string => {
    return `${value.Property_Code}${separator}${value.UnitType}`;
  },
});

export const occupancy = new schema.Entity('Occupancy', undefined, {
  idAttribute: (value: any, parent: any, key: string): string => {
    return `${value.Property_Code}${separator}${value.UnitType}`;
  },
});

export const leasePriceDetails = new schema.Entity(
  'leasePriceDetails',
  undefined,
  {
    idAttribute: (value: any, parent: any, key: string): string => {
      return `${value.Property_Code}${separator}${value.UnitType_Code}`;
    },
    processStrategy: (value: any, parent: any, key: string) => {
      let processData: any = {
        Demand: value.Demand,
        // Occupancy: value.Occupancy,
        NewLeasePrice: value.NewLeasePrice,
        newOccupancyKpi: value.newOccupancyKpi,
      };
      return processData;
    },
  }
);

export const leasePriceMinimized = new schema.Entity(
  'leasePriceKeys',
  undefined,
  {
    idAttribute: (value: any, parent: any, key: string): string => {
      return `${value.Property_Code}${separator}${value.UnitType_Code}`;
    },
    processStrategy: (value: any, parent: any, key: string) => {
      let bestTerm = sortBy(value.NewLeasePrice, (obj: any) => obj.Price)[0];
      let processData: any = {
        Property_Code: value.Property_Code,
        UnitType: value.UnitType_Code,
        Description: value.Description,
        Beds: value.Beds,
        availableUnits: value.availableUnits,
        occupiedUnits: value.occupiedUnits,
        totalUnits: value.totalUnits,
        vacantUnits: value.vacantUnits,
        SQFT: value.SQFT,
        Rent: value.Rent,
        bestOffer: bestTerm.Price,
        bestTerm: bestTerm.Term,
        UploadOfferUnitType:
          value.NewLeasePrice[0].UnitType ?? value.UnitType_Code,
      };
      processData.IsApproved = value.NewLeasePrice.every(
        (ele: any) => ele.IsApproved
      );
      processData.IsExcluded = value.NewLeasePrice.every(
        (ele: any) => ele.IsExcluded
      );
      processData.IsUpdated = value.NewLeasePrice.every(
        (ele: any) => ele.IsUpdated
      );
      processData.IsUploaded = value.NewLeasePrice.every(
        (ele: any) => ele.IsUploaded
      );
      processData.IsFlagged = value.IsFlagged;
      processData.FlagDetails = value.FlagDetails ?? undefined;
      processData.Strategy = first(value.NewLeasePrice as any[])?.Strategy;
      return processData;
    },
  }
);
